import { adminApi } from '@core/api/httpClient'
import type { PaginatedResponse } from '@shared/types/pagination'
import type {
  AuditLogDto,
  CreatePropertySettingPayloadDto,
  PropertyAmenityItemDto,
  PropertyCoOwnerDto,
  PropertyOwnershipSourceDto,
  PropertyCoOwnerPoaDto,
  CreatePropertyCoOwnerPoaPayloadDto,
  UpdatePropertyCoOwnerPoaPayloadDto,
  PropertyDocumentItemDto,
  PropertyDocumentPayloadDto,
  PropertyFinancialsDto,
  PropertyHighlightItemDto,
  PropertyMediaItemDto,
  PropertyMediaPayloadDto,
  PropertyMutationResponseDto,
  PropertyOfferingDto,
  PropertyValuationDto,
  RefundItemDto,
  RentalPeriodDto,
} from './propertyContracts'
import type {
  AddPropertyInput,
  AuditLog,
  CreatePropertySettingInput,
  DelistPropertyInput,
  PropertiesApiParams,
  PropertiesListResponse,
  Property,
  PropertyAmenityItem,
  PropertyCoOwner,
  PropertyOwnershipSource,
  PropertyCoOwnerPoa,
  CreatePropertyCoOwnerPoaInput,
  UpdatePropertyCoOwnerPoaInput,
  SaveOwnershipInput,
  PropertyDocumentItem,
  PropertyFinancials,
  PropertyHighlightItem,
  PropertyMasterItem,
  PropertyMediaItem,
  PropertyActivityEvent,
  PropertyActivityFilters,
  PropertyOffering,
  PropertySetting,
  PropertyValuation,
  PropertyValuationInput,
  RefundItem,
  RejectPropertyInput,
  UpdatePropertyInput,
  RentalPeriod,
  CreateRentalPeriodInput,
  RequestRevisionInput,
  ArchivePropertyInput,
  PropertyVaspOnboarding,
} from '../types/propertyTypes'
import {
  extractTabArray,
  parseAuditLog,
  parsePropertyActivityResponse,
  parseVaspOnboarding,
  parseProperty,
  parsePropertiesListResponse,
  parsePropertyAmenityItem,
  parsePropertyCoOwner,
  parsePropertyCoOwnerPoa,
  parsePropertyCoOwnerPoaList,
  parsePropertyDocumentItem,
  parsePropertyFinancials,
  parsePropertyHighlightItem,
  parsePropertyMastersResponse,
  parsePropertyMediaItem,
  parsePropertyOffering,
  parsePropertyOwnershipSource,
  parsePropertySettingsResponse,
  parsePropertyValuation,
  parseRefundItem,
  parseRentalPeriod,
} from './propertyParser'
import { PROPERTY_STATUS } from '../constants/propertyStatus'

const P = '/api/admin/properties'
// The listing-requests controller, for the one ownership-source read below.
const LISTING_REQUESTS = '/api/admin/listing-requests'
const MASTERS = '/api/admin/masters'

// ── List / CRUD ───────────────────────────────────────────────────────────────

export async function getPropertyList(
  params: PropertiesApiParams = {},
): Promise<PropertiesListResponse> {
  const q = new URLSearchParams()
  if (params.page) q.append('page', String(params.page))
  if (params.limit) q.append('limit', String(params.limit))
  if (params.search?.trim()) q.append('search', params.search.trim())
  // API uses camelCase query params on this endpoint
  if (params.propertyType) q.append('propertyType', params.propertyType)
  if (params.propertyStatus) q.append('propertyStatus', params.propertyStatus)
  if (params.rentalStatus) q.append('rentalStatus', params.rentalStatus)
  if (params.offeringStatus) q.append('offeringStatus', params.offeringStatus)
  if (params.marketId) q.append('marketId', params.marketId)
  if (params.countryId) q.append('countryId', params.countryId)
  if (params.propertyOwnerId) q.append('propertyOwnerId', params.propertyOwnerId)
  // Completeness flag filters (lifecycle §4 Queues 1 & 2). Only appended when
  // explicitly set so other list views are unaffected.
  if (params.opsEnrichmentComplete !== undefined)
    q.append('opsEnrichmentComplete', String(params.opsEnrichmentComplete))
  if (params.financialsComplete !== undefined)
    q.append('financialsComplete', String(params.financialsComplete))
  const qs = q.toString()
  const { data } = await adminApi.get<unknown>(`${P}${qs ? `?${qs}` : ''}`)
  return parsePropertiesListResponse(data)
}

export async function getProperty(id: string): Promise<Property> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}`)
  const inner = (data as Record<string, unknown>)?.data ?? data
  return parseProperty(inner)
}

export async function addProperty(payload: AddPropertyInput): Promise<PropertyMutationResponseDto> {
  const { data } = await adminApi.post<PropertyMutationResponseDto>(P, payload)
  return data
}

export async function updateProperty(
  id: string,
  payload: UpdatePropertyInput,
): Promise<PropertyMutationResponseDto> {
  const { data } = await adminApi.patch<PropertyMutationResponseDto>(`${P}/${id}`, payload)
  return data
}

export async function deleteProperty(id: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await adminApi.delete<{ success: boolean; message?: string }>(`${P}/${id}`)
  return data
}

// ── Masters ───────────────────────────────────────────────────────────────────

export async function getPropertyMarkets(): Promise<PropertyMasterItem[]> {
  const { data } = await adminApi.get<unknown>(`${MASTERS}/markets`)
  return parsePropertyMastersResponse(data).data
}

export async function getPropertyCountries(): Promise<PropertyMasterItem[]> {
  const { data } = await adminApi.get<unknown>(`${MASTERS}/countries`)
  return parsePropertyMastersResponse(data).data
}

// ── Status actions ─────────────────────────────────────────────────────────────

export async function submitProperty(id: string): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(`${P}/${id}/submit`)
  return data
}

export async function approveProperty(id: string): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(`${P}/${id}/approve`)
  return data
}

// ── Lifecycle transitions (property-lifecycle-plan.md §5) ───────────────────────
// All PATCH /api/admin/properties/{id}/<action>. Backend enforces guards via the
// can*/availableTransitions flags on the Property DTO — the FE only gates buttons.

// submitted → in_verification. No body. BE 422s unless both completeness flags true.
export async function submitForVerification(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/submit-for-verification`,
  )
  return data
}

// in_verification → in_compliance. No body.
export async function verifyApproveProperty(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/verify-approve`,
  )
  return data
}

// in_verification → revision_requested. Body { reason, notifyRoles? }.
export async function requestPropertyRevision(
  id: string,
  input: RequestRevisionInput,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/request-revision`,
    { reason: input.reason, notifyRoles: input.notifyRoles },
  )
  return data
}

// in_verification → draft (send back for revision). No dedicated transition
// route — uses the generic update endpoint with a target status. The BE resets
// ops_enrichment_complete & financials_complete on this transition so the
// property re-enters the Ops Enrichment + Financial Setup queues.
export async function sendPropertyBackToDraft(id: string): Promise<PropertyMutationResponseDto> {
  const { data } = await adminApi.patch<PropertyMutationResponseDto>(`${P}/${id}`, {
    propertyStatus: PROPERTY_STATUS.DRAFT,
  })
  return data
}

// revision_requested → submitted. No body.
export async function resubmitProperty(id: string): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/resubmit`,
  )
  return data
}

// in_compliance → in_vasp. No body. BE auto-creates the VASP asset + calls provider.
export async function complianceApproveProperty(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/compliance-approve`,
  )
  return data
}

// in_compliance → rejected. Body { reason }.
export async function complianceRejectProperty(
  id: string,
  input: RejectPropertyInput,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/compliance-reject`,
    { reason: input.reason },
  )
  return data
}

// PATCH /vasp/sync — re-reads all three tiers (vehicle, instrument, asset) from
// the provider using the stored instrument + asset ids, persists the per-tier
// statuses, and auto-flips to approved on provider success. Same response shape
// as onboard / retry. Requires a stored instrument id — a vehicle-only partial
// has no read path and 404s, so the caller hides Sync for those rows.
export async function syncPropertyVasp(id: string): Promise<PropertyVaspOnboarding> {
  const { data } = await adminApi.patch<unknown>(`${P}/${id}/vasp/sync`)
  return parseVaspOnboarding(data)
}

// PATCH /vasp/onboard — runs the three-tier (vehicle → instrument → asset)
// onboarding through the gateway for an IN_VASP property. Returns the per-tier
// statuses + umbrella currentStatus; BE auto-flips IN_VASP → APPROVED on full
// success, or returns a partial with `failureStatus` set to the failed tier.
export async function onboardPropertyVasp(id: string): Promise<PropertyVaspOnboarding> {
  const { data } = await adminApi.patch<unknown>(`${P}/${id}/vasp/onboard`)
  return parseVaspOnboarding(data)
}

// PATCH /vasp/retry — replays the persisted ids + per-tier statuses so the
// gateway resumes only the tier that previously failed. Same response shape.
export async function retryPropertyVaspOnboarding(id: string): Promise<PropertyVaspOnboarding> {
  const { data } = await adminApi.patch<unknown>(`${P}/${id}/vasp/retry`)
  return parseVaspOnboarding(data)
}

// approved → archived. Body { reason? }. BE 422 PROPERTY_HAS_ACTIVE_OFFERING if active offerings.
export async function archiveProperty(
  id: string,
  input: ArchivePropertyInput = {},
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/archive`,
    input.reason ? { reason: input.reason } : {},
  )
  return data
}

// PATCH /api/admin/properties/{id}/compliance-check — marks compliance check as passed
// Transitions status: submitted → in_compliance
export async function complianceCheckProperty(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/compliance-check`,
  )
  return data
}

// PATCH /api/admin/properties/{id}/financial-setup — moves property to financial setup
// Transitions status: in_compliance → in_financial_setup
export async function financialSetupProperty(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/financial-setup`,
  )
  return data
}

export async function sendPropertyToVasp(id: string): Promise<void> {
  await adminApi.patch(`${P}/${id}/send-to-vasp`)
}

export async function syncPropertyVaspStatus(id: string): Promise<void> {
  await adminApi.post(`${P}/${id}/sync-vasp-status`)
}

export async function bypassPropertyVasp(id: string): Promise<void> {
  await adminApi.patch(`${P}/${id}/bypass-vasp`, { reason: 'Bypass VASP - Manual override' })
}

export async function rejectProperty(
  id: string,
  input: RejectPropertyInput,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/reject`,
    { reason: input.reason },
  )
  return data
}

export async function delistProperty(
  id: string,
  input: DelistPropertyInput,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.patch<{ success: boolean; message: string }>(
    `${P}/${id}/delist`,
    { reason: input.reason, scenario: input.scenario },
  )
  return data
}

export async function assignProperty(
  id: string,
  subAdminId: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.post<{ success: boolean; message: string }>(`${P}/${id}/assign`, {
    subAdminId,
  })
  return data
}

// ── Audit logs & refunds ──────────────────────────────────────────────────────

export async function getPropertyAuditLogs(id: string): Promise<AuditLog[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/audit-logs`)
  return (extractTabArray(data) as AuditLogDto[]).map(parseAuditLog)
}

// Unified activity feed — paginated merge of status / rental / valuation
// events. Optional `type` filters to one source server-side.
export async function getPropertyActivity(
  id: string,
  filters: PropertyActivityFilters = {},
): Promise<PaginatedResponse<PropertyActivityEvent>> {
  const q = new URLSearchParams()
  if (filters.type) q.append('type', filters.type)
  if (filters.page) q.append('page', String(filters.page))
  if (filters.limit) q.append('limit', String(filters.limit))
  const qs = q.toString()
  const { data } = await adminApi.get<unknown>(`${P}/${id}/activity${qs ? `?${qs}` : ''}`)
  return parsePropertyActivityResponse(data)
}

export async function getPropertyRefunds(id: string): Promise<RefundItem[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/refunds`)
  return (extractTabArray(data) as RefundItemDto[]).map(parseRefundItem)
}

export async function initiateAllRefunds(
  id: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.post<{ success: boolean; message: string }>(
    `${P}/${id}/refunds/initiate-all`,
  )
  return data
}

export async function retryRefund(
  propertyId: string,
  refundId: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await adminApi.post<{ success: boolean; message: string }>(
    `${P}/${propertyId}/refunds/${refundId}/retry`,
  )
  return data
}

// ── Tab: financials ───────────────────────────────────────────────────────────

export async function getPropertyFinancials(id: string): Promise<PropertyFinancials | null> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/financials`)
  const d = data as Record<string, unknown>
  const inner = (d?.data ?? d) as PropertyFinancialsDto | null | undefined
  if (!inner || (typeof inner === 'object' && Object.keys(inner).length === 0)) return null
  return parsePropertyFinancials(inner)
}

export async function upsertPropertyFinancials(
  propertyId: string,
  subId: string | null | undefined,
  // Partial so callers can send only the fields they own (e.g. the create
  // wizard's purchasePrice + purchaseDate). The BE writes only present keys, so
  // a partial PATCH preserves the financials fields it doesn't include.
  payload: Partial<PropertyFinancials>,
): Promise<unknown> {
  // Drop every field the backend owns. Two reasons, both ending in a 400 or a
  // pointless round-trip:
  //
  //   - the row id and approval trail are not on the request DTO at all, and the
  //     API runs forbidNonWhitelisted, so sending them 400s with
  //     "Validation failed";
  //   - the service charge, offering value and yields stopped being stored
  //     columns when fee configuration took over and are now derived from the
  //     property's fee model on every read. The DTO still accepts them so older
  //     clients are not rejected, but the write path whitelists columns and
  //     discards them, so sending them only risks the two coming back out of
  //     sync.
  //
  // Read all of them back from the response instead.
  const {
    id,
    financialsApprovedAt,
    financialsApprovedBy,
    serviceChargePerSqm,
    annualServiceChargeTotal,
    totalOfferingValue,
    grossYieldPercentage,
    netYieldPercentage,
    ...body
  } = payload
  void id
  void financialsApprovedAt
  void financialsApprovedBy
  void serviceChargePerSqm
  void annualServiceChargeTotal
  void totalOfferingValue
  void grossYieldPercentage
  void netYieldPercentage
  if (subId) {
    const { data } = await adminApi.patch(`${P}/${propertyId}/financials`, body)
    return data
  }
  const { data } = await adminApi.post(`${P}/${propertyId}/financials`, body)
  return data
}

// Force-set properties.financials_complete = true (Fin Admin confirms financials
// setup is done on the wizard Review step). 204 No Content. Sends an empty body
// so the PATCH carries a JSON payload even though the endpoint takes no fields.
export async function markFinancialsComplete(propertyId: string): Promise<void> {
  await adminApi.patch(`${P}/${propertyId}/financials-complete`, {})
}

// Force-set properties.ops_enrichment_complete = true (Ops Admin confirms
// enrichment is done on the wizard Review step). 204 No Content. 422 when the
// server-side completeness check (cover image + highlight + amenity) fails.
export async function markOpsEnrichmentComplete(propertyId: string): Promise<void> {
  await adminApi.patch(`${P}/${propertyId}/ops-enrichment-complete`, {})
}

// ── Tab: co-owners ────────────────────────────────────────────────────────────

/**
 * Whether this property's ownership belongs to a listing request.
 *
 * Lives here, and not in the `listingRequests` module that owns the route, because
 * imports flow downward only (architecture-principles §11): `listingRequests`
 * already imports `@modules/properties`, so a hook exported from there and consumed
 * here would close the cycle. The path constant is duplicated rather than imported
 * for the same reason.
 */
export async function getPropertyOwnershipSource(id: string): Promise<PropertyOwnershipSource> {
  const { data } = await adminApi.get<unknown>(`${LISTING_REQUESTS}/by-property/${id}`)
  const inner = (data as Record<string, unknown>)?.data ?? data
  return parsePropertyOwnershipSource(inner as PropertyOwnershipSourceDto)
}

export async function getPropertyCoOwners(id: string): Promise<PropertyCoOwner[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/co-owners`)
  return (extractTabArray(data) as PropertyCoOwnerDto[]).map(parsePropertyCoOwner)
}

export async function upsertPropertyCoOwner(
  propertyId: string,
  subId: string | null | undefined,
  payload: PropertyCoOwner,
): Promise<unknown> {
  // BE accepts a camelCase body and whitelists exactly these fields
  // (forbidNonWhitelisted). bankAccountId is an optional UUID — omit it when
  // empty so an absent bank account doesn't fail UUID validation.
  const body: Record<string, unknown> = {
    propertyOwnerId: payload.propertyOwnerId,
    ownershipPercentage: payload.ownershipPercentage,
    isPrimary: payload.isPrimary,
  }
  if (payload.bankAccountId) body.bankAccountId = payload.bankAccountId
  if (subId) {
    const { data } = await adminApi.patch(`${P}/${propertyId}/co-owners/${subId}`, body)
    return data
  }
  const { data } = await adminApi.post(`${P}/${propertyId}/co-owners`, body)
  return data
}

export async function deletePropertyCoOwner(propertyId: string, ownerId: string): Promise<unknown> {
  const { data } = await adminApi.delete(`${P}/${propertyId}/co-owners/${ownerId}`)
  return data
}

// ── Power of Attorney (POA) — nested under the holder co-owner ──────────────────
// Base: /api/admin/properties/:propertyId/co-owners/:coOwnerId/poa
// A holder co-owner (coOwnerPoaId) represents one or more other co-owners; one
// POA row per represented co-owner, each carrying its own POA document ids.

function poaBase(propertyId: string, coOwnerId: string): string {
  return `${P}/${propertyId}/co-owners/${coOwnerId}/poa`
}

export async function getPropertyCoOwnerPoaList(
  propertyId: string,
  coOwnerId: string,
): Promise<PropertyCoOwnerPoa[]> {
  const { data } = await adminApi.get<unknown>(poaBase(propertyId, coOwnerId))
  return parsePropertyCoOwnerPoaList(data)
}

// Aggregates POA rows across every holder co-owner of a property. There is no
// property-wide POA listing endpoint — POAs are nested per holder co-owner — so
// fan out over each holder's list and flatten. Used by the create wizard to
// repopulate already-saved POAs when an operator resumes a draft.
export async function getPropertyCoOwnerPoas(
  propertyId: string,
  coOwnerIds: string[],
): Promise<PropertyCoOwnerPoa[]> {
  if (!coOwnerIds.length) return []
  const lists = await Promise.all(
    coOwnerIds.map((coOwnerId) => getPropertyCoOwnerPoaList(propertyId, coOwnerId)),
  )
  return lists.flat()
}

export async function getPropertyCoOwnerPoa(
  propertyId: string,
  coOwnerId: string,
  poaId: string,
): Promise<PropertyCoOwnerPoa> {
  const { data } = await adminApi.get<unknown>(`${poaBase(propertyId, coOwnerId)}/${poaId}`)
  const inner = (data as Record<string, unknown>)?.data ?? data
  return parsePropertyCoOwnerPoa(inner as PropertyCoOwnerPoaDto)
}

export async function createPropertyCoOwnerPoa(
  propertyId: string,
  coOwnerId: string,
  input: CreatePropertyCoOwnerPoaInput,
): Promise<unknown> {
  const payload: CreatePropertyCoOwnerPoaPayloadDto = {
    coOwnerPoaId: input.coOwnerPoaId,
    representsCoOwnerId: input.representsCoOwnerId,
    poaDocumentId: input.poaDocumentId,
  }
  const { data } = await adminApi.post(poaBase(propertyId, coOwnerId), payload)
  return data
}

export async function updatePropertyCoOwnerPoa(
  propertyId: string,
  coOwnerId: string,
  poaId: string,
  input: UpdatePropertyCoOwnerPoaInput,
): Promise<unknown> {
  const payload: UpdatePropertyCoOwnerPoaPayloadDto = {
    poaDocumentId: input.poaDocumentId,
  }
  const { data } = await adminApi.patch(`${poaBase(propertyId, coOwnerId)}/${poaId}`, payload)
  return data
}

export async function deletePropertyCoOwnerPoa(
  propertyId: string,
  coOwnerId: string,
  poaId: string,
): Promise<unknown> {
  const { data } = await adminApi.delete(`${poaBase(propertyId, coOwnerId)}/${poaId}`)
  return data
}

// ── Bulk ownership save (create-wizard Step 3) ──────────────────────────────────
// Persists co-owners + POA assignments + POA documents in ONE transactional
// multipart request. The BE upserts the co-owner set, resolves propertyOwnerId →
// record id, uploads each POA document into user_documents, and creates the
// property_co_owner_poa rows — all-or-none. A POA's document is carried as a
// `poaFile_<n>` part, referenced from the matching `poas[i].fileIndex`.
export async function savePropertyOwnership(
  propertyId: string,
  input: SaveOwnershipInput,
): Promise<unknown> {
  const form = new FormData()
  form.append(
    'coOwners',
    JSON.stringify(
      input.coOwners.map((c) => ({
        propertyOwnerId: c.propertyOwnerId,
        ownershipPercentage: c.ownershipPercentage,
        isPrimary: c.isPrimary,
        ...(c.bankAccountId ? { bankAccountId: c.bankAccountId } : {}),
      })),
    ),
  )
  let fileIndex = 0
  const poas = input.poas.map((p) => {
    const row: Record<string, unknown> = {
      holderPropertyOwnerId: p.holderPropertyOwnerId,
      representsPropertyOwnerId: p.representsPropertyOwnerId,
    }
    if (p.documentTitle) row.documentTitle = p.documentTitle
    if (p.document) {
      const idx = fileIndex++
      form.append(`poaFile_${idx}`, p.document)
      row.fileIndex = idx
    }
    return row
  })
  form.append('poas', JSON.stringify(poas))
  const { data } = await adminApi.postForm(`${P}/${propertyId}/ownership`, form)
  return data
}

// ── Tab: media ────────────────────────────────────────────────────────────────

export async function getPropertyMedia(id: string): Promise<PropertyMediaItem[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/media`)
  return (extractTabArray(data) as PropertyMediaItemDto[]).map(parsePropertyMediaItem)
}

export async function upsertPropertyMedia(
  propertyId: string,
  subId: string | null | undefined,
  payload: PropertyMediaPayloadDto | FormData,
): Promise<unknown> {
  // Use postForm/patchForm (Axios 1.x) so Axios sets multipart/form-data + boundary correctly.
  // The plain post/patch defaults to Content-Type: application/json which breaks server parsing.
  if (subId) {
    const { data } = await adminApi.patchForm(`${P}/${propertyId}/media/${subId}`, payload)
    return data
  }
  const { data } = await adminApi.postForm(`${P}/${propertyId}/media`, payload)
  return data
}

export async function deletePropertyMedia(propertyId: string, mediaId: string): Promise<unknown> {
  const { data } = await adminApi.delete(`${P}/${propertyId}/media/${mediaId}`)
  return data
}

// ── Tab: documents ────────────────────────────────────────────────────────────

export async function getPropertyDocuments(id: string): Promise<PropertyDocumentItem[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/documents`)
  return (extractTabArray(data) as PropertyDocumentItemDto[]).map(parsePropertyDocumentItem)
}

export async function upsertPropertyDocument(
  propertyId: string,
  subId: string | null | undefined,
  payload: PropertyDocumentPayloadDto | FormData,
): Promise<unknown> {
  if (subId) {
    const { data } = await adminApi.patchForm(`${P}/${propertyId}/documents/${subId}`, payload)
    return data
  }
  const { data } = await adminApi.postForm(`${P}/${propertyId}/documents`, payload)
  return data
}

export async function deletePropertyDocument(
  propertyId: string,
  documentId: string,
): Promise<unknown> {
  const { data } = await adminApi.delete(`${P}/${propertyId}/documents/${documentId}`)
  return data
}

// ── Tab: amenities ────────────────────────────────────────────────────────────

export async function getPropertyAmenities(id: string): Promise<PropertyAmenityItem[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/amenities`)
  return (extractTabArray(data) as PropertyAmenityItemDto[]).map(parsePropertyAmenityItem)
}

export async function upsertPropertyAmenity(
  propertyId: string,
  subId: string | null | undefined,
  payload: PropertyAmenityItem,
): Promise<unknown> {
  if (subId) {
    const { data } = await adminApi.patch(`${P}/${propertyId}/amenities/${subId}`, payload)
    return data
  }
  const { data } = await adminApi.post(`${P}/${propertyId}/amenities`, payload)
  return data
}

export async function deletePropertyAmenity(
  propertyId: string,
  amenityRecordId: string,
): Promise<unknown> {
  const { data } = await adminApi.delete(`${P}/${propertyId}/amenities/${amenityRecordId}`)
  return data
}

// ── Tab: highlights ───────────────────────────────────────────────────────────

export async function getPropertyHighlights(id: string): Promise<PropertyHighlightItem[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/highlights`)
  return (extractTabArray(data) as PropertyHighlightItemDto[]).map(parsePropertyHighlightItem)
}

export async function upsertPropertyHighlight(
  propertyId: string,
  subId: string | null | undefined,
  payload: PropertyHighlightItem,
): Promise<unknown> {
  if (subId) {
    const { data } = await adminApi.patch(`${P}/${propertyId}/highlights/${subId}`, payload)
    return data
  }
  const { data } = await adminApi.post(`${P}/${propertyId}/highlights`, payload)
  return data
}

export async function deletePropertyHighlight(
  propertyId: string,
  highlightRecordId: string,
): Promise<unknown> {
  const { data } = await adminApi.delete(`${P}/${propertyId}/highlights/${highlightRecordId}`)
  return data
}

// ── Tab: valuations ───────────────────────────────────────────────────────────

export async function getPropertyValuations(id: string): Promise<PropertyValuation[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/valuations`)
  return (extractTabArray(data) as PropertyValuationDto[]).map(parsePropertyValuation)
}

export async function upsertPropertyValuation(
  propertyId: string,
  _subId: string | null | undefined,
  payload: PropertyValuationInput,
): Promise<unknown> {
  // API only supports POST for valuations (no PATCH endpoint) — each call records a new valuation.
  const { data } = await adminApi.post(`${P}/${propertyId}/valuations`, payload)
  return data
}

// ── Rental periods (property-lifecycle-plan.md §3 step 2) ───────────────────────

export async function getRentalPeriods(propertyId: string): Promise<RentalPeriod[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${propertyId}/rental-periods`)
  return (extractTabArray(data) as RentalPeriodDto[]).map(parseRentalPeriod)
}

export async function createRentalPeriod(
  propertyId: string,
  input: CreateRentalPeriodInput,
): Promise<RentalPeriod> {
  // BE expects a camelCase body on this endpoint (CreatePropertyRentalPeriodRequestDto).
  // CreateRentalPeriodInput already matches that shape, so send it directly —
  // undefined keys are dropped by JSON serialization; nulls are accepted as optional.
  const payload: CreateRentalPeriodInput = {
    status: input.status,
    tenantType: input.tenantType,
    monthlyRentAmount: input.monthlyRentAmount ?? null,
    annualRentAmount: input.annualRentAmount ?? null,
    leaseStart: input.leaseStart ?? null,
    leaseEnd: input.leaseEnd ?? null,
    rentAgreementDocumentId: input.rentAgreementDocumentId ?? null,
    source: input.source ?? null,
    notes: input.notes ?? null,
  }
  const { data } = await adminApi.post<unknown>(`${P}/${propertyId}/rental-periods`, payload)
  const inner = (data as Record<string, unknown>)?.data ?? data
  return parseRentalPeriod(inner)
}

// ── Tab: offerings ────────────────────────────────────────────────────────────

export async function getPropertyOfferings(id: string): Promise<PropertyOffering[]> {
  const { data } = await adminApi.get<unknown>(`${P}/${id}/offerings`)
  return (extractTabArray(data) as PropertyOfferingDto[]).map(parsePropertyOffering)
}

export async function upsertPropertyOffering(
  propertyId: string,
  subId: string | null | undefined,
  payload: PropertyOffering,
): Promise<unknown> {
  if (subId) {
    const { data } = await adminApi.patch(`${P}/${propertyId}/offerings/${subId}`, payload)
    return data
  }
  const { data } = await adminApi.post(`${P}/${propertyId}/offerings`, payload)
  return data
}

// ── Offering actions ──────────────────────────────────────────────────────────

const O = '/api/admin/offerings'

export async function submitOffering(id: string): Promise<unknown> {
  const { data } = await adminApi.patch(`${O}/${id}/submit`)
  return data
}

export async function approveOffering(id: string): Promise<unknown> {
  const { data } = await adminApi.patch(`${O}/${id}/approve`)
  return data
}

export async function rejectOffering(id: string, payload: { reason: string }): Promise<unknown> {
  const { data } = await adminApi.patch(`${O}/${id}/reject`, payload)
  return data
}

export async function sendOfferingToVasp(id: string): Promise<unknown> {
  const { data } = await adminApi.patch(`${O}/${id}/send-to-vasp`)
  return data
}

// ── Settings ──────────────────────────────────────────────────────────────────

export async function getPropertySettings(propertyId?: string): Promise<PropertySetting[]> {
  const url = propertyId
    ? `${MASTERS}/property-settings?property_id=${propertyId}`
    : `${MASTERS}/property-settings`
  const { data } = await adminApi.get<unknown>(url)
  return parsePropertySettingsResponse(data)
}

export async function createPropertySetting(
  input: CreatePropertySettingInput,
): Promise<PropertyMutationResponseDto> {
  const payload: CreatePropertySettingPayloadDto = {
    property_id: input.propertyId,
    effective_from: input.effectiveFrom,
    min_investment_amount: input.minInvestmentAmount,
    max_ownership_pct_per_property: input.maxOwnershipPctPerProperty,
    default_funding_deadline_days: input.defaultFundingDeadlineDays,
    primary_lock_period_days: input.primaryLockPeriodDays,
    secondary_lock_period_days: input.secondaryLockPeriodDays,
    kyc_expiry_days: input.kycExpiryDays,
  }
  const { data } = await adminApi.post<PropertyMutationResponseDto>(
    `${MASTERS}/property-settings`,
    payload,
  )
  return data
}

// ── Status counts ─────────────────────────────────────────────────────────────

export interface PropertyStatusCountsDto {
  all: number
  draft: number
  inFinancialSetup: number
  inVerification: number
  inCompliance: number
  inVasp: number
  approved: number
  rejected: number
  archived: number
  /** Approved properties currently surfaced in the Rental Refresh queue. */
  rentalRefresh: number
  /** Approved properties currently surfaced in the Valuation Refresh queue. */
  valuationRefresh: number
}

export async function getPropertyStatusCounts(): Promise<PropertyStatusCountsDto> {
  const { data } = await adminApi.get<unknown>(`${P}/status-counts`)
  return ((data as Record<string, unknown>)?.data ?? data) as PropertyStatusCountsDto
}

// ── Refresh queues (property-refresh-queues-plan.md) ──────────────────────────

import {
  parseRentalRefreshCounts,
  parseRentalRefreshQueueResponse,
  parseValuationRefreshCounts,
  parseValuationRefreshQueueResponse,
} from './propertyParser'
import type {
  RentalRefreshCounts,
  RentalRefreshQueueFilters,
  RentalRefreshQueueResponse,
  ValuationRefreshCounts,
  ValuationRefreshQueueFilters,
  ValuationRefreshQueueResponse,
} from '../types/propertyTypes'

function appendQueueFilters(
  q: URLSearchParams,
  filters: {
    search?: string
    marketId?: string
    countryId?: string
    page?: number
    limit?: number
  },
): void {
  if (filters.search?.trim()) q.append('search', filters.search.trim())
  if (filters.marketId) q.append('marketId', filters.marketId)
  if (filters.countryId) q.append('countryId', filters.countryId)
  if (filters.page) q.append('page', String(filters.page))
  if (filters.limit) q.append('limit', String(filters.limit))
}

export async function getRentalRefreshQueue(
  filters: RentalRefreshQueueFilters = {},
): Promise<RentalRefreshQueueResponse> {
  const q = new URLSearchParams()
  if (filters.bucket) q.append('bucket', filters.bucket)
  appendQueueFilters(q, filters)
  const qs = q.toString()
  const { data } = await adminApi.get<unknown>(`${P}/rental-refresh-queue${qs ? `?${qs}` : ''}`)
  return parseRentalRefreshQueueResponse(data)
}

export async function getRentalRefreshCounts(): Promise<RentalRefreshCounts> {
  const { data } = await adminApi.get<unknown>(`${P}/rental-refresh-counts`)
  return parseRentalRefreshCounts(data)
}

export async function getValuationRefreshQueue(
  filters: ValuationRefreshQueueFilters = {},
): Promise<ValuationRefreshQueueResponse> {
  const q = new URLSearchParams()
  if (filters.bucket) q.append('bucket', filters.bucket)
  if (filters.hasActiveOffering !== undefined) {
    q.append('hasActiveOffering', String(filters.hasActiveOffering))
  }
  appendQueueFilters(q, filters)
  const qs = q.toString()
  const { data } = await adminApi.get<unknown>(`${P}/valuation-refresh-queue${qs ? `?${qs}` : ''}`)
  return parseValuationRefreshQueueResponse(data)
}

export async function getValuationRefreshCounts(): Promise<ValuationRefreshCounts> {
  const { data } = await adminApi.get<unknown>(`${P}/valuation-refresh-counts`)
  return parseValuationRefreshCounts(data)
}

export async function voidPropertyValuation(
  propertyId: string,
  valuationId: string,
  reason: string,
): Promise<void> {
  await adminApi.post(`${P}/${propertyId}/valuations/${valuationId}/void`, { reason })
}
